import React, { MutableRefObject, useState, useEffect } from "react";
import "./RoomPage.scss";
import { createBrowserHistory } from "history";
import DiceBar from "./DiceBar";
import LinkBox from "./LinkBox";
import ConnectedUsers from "./ConnectedUsers";
import RollResultRow from "./RollResultRow";
import {
  ROLL_REQUEST_EVENT,
  ROOM_WELCOME_EVENT,
  ROLL_RESULT_EVENT,
  USER_CONNECTED_EVENT,
  USER_DISCONNECTED_EVENT,
} from "../constants/events";
import {
  Message,
  RollRequest,
  DiceToRoll,
  RollResult,
  RoomWelcome,
  User,
  UserConnected,
  UserDisconnected,
} from "../models/messages";

interface Props {
  initialAccessCode: string | undefined;
  ws: MutableRefObject<WebSocket>;
}

const performRoll = (ws: WebSocket, diceToRoll: DiceToRoll) => {
  const rollRequest: Message<RollRequest> = {
    event: ROLL_REQUEST_EVENT,
    data: {
      dice_to_roll: diceToRoll,
    },
  };

  ws.send(JSON.stringify(rollRequest));
};

export default function RoomPage(props: Props) {
  const { initialAccessCode, ws } = props;

  const [accessCode, setAccessCode] = useState<string>();
  const [messages, setMessages] = useState<Array<any>>([]);
  const [rollHistory, setRollHistory] = useState<RollResult[]>([]);
  const [users, setUsers] = useState<{ [keys: string]: User }>({});
  const [user, setUser] = useState<User>();

  useEffect(() => {
    ws.current.onmessage = (e) => {
      const message = JSON.parse(e.data) as Message<any>;

      if (message.event === ROOM_WELCOME_EVENT) {
        const roomWelcome = message.data as RoomWelcome;

        const newAccessCode = roomWelcome.access_code;
        if (accessCode !== newAccessCode) {
          createBrowserHistory().push(`/${newAccessCode}`);
        }
        setAccessCode(message?.data?.access_code);

        let preExistingUsers: { [keys: string]: User } = {};

        roomWelcome.users.forEach((user) => {
          preExistingUsers[user.id] = user;
        });
        setUsers(preExistingUsers);

        setUser(roomWelcome.you);

        setRollHistory(roomWelcome.roll_history);
      } else if (message.event === ROLL_RESULT_EVENT) {
        const rollResult = message.data as RollResult;

        setRollHistory((prevState) => [rollResult, ...prevState]);
      } else if (message.event === USER_CONNECTED_EVENT) {
        const userConnected = message.data as UserConnected;

        setUsers((prevState) => ({ ...prevState, [userConnected.user.id]: userConnected.user }));
      } else if (message.event === USER_DISCONNECTED_EVENT) {
        const userDisconnected = message.data as UserDisconnected;

        setUsers((prevState) => ({ ...prevState, [userDisconnected.user.id]: userDisconnected.user }));
      }
    };

    return () => {
      ws.current.close();
    };
  }, [initialAccessCode]);

  return (
    <div className=".c-room-page container my-3">
      <div className="row h-100">
        <aside className="col col-md-3 col-lg-4 col-xl-3 order-1 order-md-1 mt-2 px-3 py-1 text-secondary">
          <LinkBox accessCode={accessCode || " "} />
          <ConnectedUsers users={Object.values(users)} />
        </aside>
        <div className="col col-md-9 col-lg-8 col-xl-9 order-2 order-md-1 px-3 pt-1 pb-3 ">
          <DiceBar userAvatar={user?.avatar} onRollPerform={(diceToRoll) => performRoll(ws.current, diceToRoll)} />
          {rollHistory.map((rollResult, i) => (
            <RollResultRow
              rollResult={rollResult}
              rollerAvatar={users[rollResult.requested_by].avatar}
              key={rollHistory.length - i}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
